
import { useState, useEffect } from "react";
import { useLocalFavorites } from "@/hooks/useLocalFavorites";
import { useLocalFavoritesData } from "@/hooks/useLocalFavoritesData";
import { VacationCard } from "@/components/VacationCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Heart, Trash2, Scale } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { VacationMascot } from "@/components/VacationMascot";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";

interface FavoritesComparisonPageProps {
  user?: User | null;
}

export const FavoritesComparisonPage = ({ user = null }: FavoritesComparisonPageProps) => {
  const navigate = useNavigate();
  const { favorites, clearFavorites } = useLocalFavorites();
  const { favoriteVacations, loading } = useLocalFavoritesData(favorites);
  const [selectedForComparison, setSelectedForComparison] = useState<number[]>([]);

  const handleSelectForComparison = (vacationId: number) => {
    if (selectedForComparison.includes(vacationId)) {
      setSelectedForComparison(prev => prev.filter(id => id !== vacationId));
    } else if (selectedForComparison.length < 3) {
      setSelectedForComparison(prev => [...prev, vacationId]);
    } else {
      toast.error("Je kunt maximaal 3 vakanties vergelijken");
    }
  };

  const handleClearFavorites = () => {
    clearFavorites();
    setSelectedForComparison([]);
    toast.success("Alle favorieten verwijderd");
  };

  const getComparisonData = () => {
    const selectedVacations = favoriteVacations.filter(vacation => 
      selectedForComparison.includes(vacation['product ID'])
    );
    
    return selectedVacations.map(vacation => ({
      id: vacation['product ID'],
      name: vacation.name,
      price: vacation.price,
      country: vacation.country,
      city: vacation.city,
      stars: vacation.stars,
      rating: vacation.rating,
      facilities: vacation.facilities,
      duration: vacation.duration,
      transportType: vacation.transportType,
      accommodationType: vacation.accommodationType || vacation.typeAccommodatie,
      aanbieder: vacation.aanbieder
    }));
  };

  const comparisonData = getComparisonData();

  // Header navigation handlers - dummy functions since we're using Header component
  const handleShowAllVacations = () => navigate('/alle-vakanties');
  const handleShowDeals = () => navigate('/deals');
  const handleShowLuxury = () => navigate('/luxe');
  const handleShowRondreizen = () => navigate('/rondreizen');
  const handleShowThema = () => navigate('/thema');
  const handleShowFavoritesComparison = () => {}; // Current page
  const handleGoHome = () => navigate('/');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
        <Header 
          onShowAllVacations={handleShowAllVacations}
          onShowDeals={handleShowDeals}
          onShowLuxury={handleShowLuxury}
          onShowRondreizen={handleShowRondreizen}
          onShowThema={handleShowThema}
          onShowFavoritesComparison={handleShowFavoritesComparison}
          onGoHome={handleGoHome}
          currentView="favorites"
        />
        
        <main className="flex-1">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Favorieten laden...</p>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
      <Header 
        onShowAllVacations={handleShowAllVacations}
        onShowDeals={handleShowDeals}
        onShowLuxury={handleShowLuxury}
        onShowRondreizen={handleShowRondreizen}
        onShowThema={handleShowThema}
        onShowFavoritesComparison={handleShowFavoritesComparison}
        onGoHome={handleGoHome}
        currentView="favorites"
      />
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-6">
            <div className="space-y-4">
              <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2 w-full md:w-auto">
                <ArrowLeft className="h-4 w-4" />
                Terug naar home
              </Button>
              
              <div className="flex items-center justify-center gap-4 sm:gap-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-600 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                  Favorieten Vergelijken
                </h1>
                <div className="flex-shrink-0">
                  <VacationMascot size="small" />
                </div>
              </div>
            </div>

            {favoriteVacations.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="h-24 w-24 mx-auto text-gray-300 mb-4" />
                <h2 className="text-2xl font-semibold text-gray-600 mb-2">Geen favorieten gevonden</h2>
                <p className="text-gray-500 mb-6">
                  Voeg eerst wat vakanties toe aan je favorieten om ze hier te kunnen vergelijken.
                </p>
                <Button onClick={() => navigate('/alle-vakanties')} className="bg-orange-600 hover:bg-orange-700">
                  Bekijk alle vakanties
                </Button>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-xl border border-red-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Scale className="h-5 w-5 text-red-600" />
                      <p className="text-red-800 font-medium">
                        Je hebt {favoriteVacations.length} favoriete {favoriteVacations.length === 1 ? 'vakantie' : 'vakanties'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="bg-white">
                        {selectedForComparison.length}/3 geselecteerd voor vergelijking
                      </Badge>
                      {favoriteVacations.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearFavorites}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Wis alle favorieten
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {selectedForComparison.length > 0 && (
                  <Card className="bg-white/80 backdrop-blur-sm border-red-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Scale className="h-5 w-5 text-red-600" />
                        Vergelijking ({selectedForComparison.length} vakanties)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="border border-gray-200 p-3 text-left font-semibold">Eigenschap</th>
                              {comparisonData.map((vacation, index) => (
                                <th key={vacation.id} className="border border-gray-200 p-3 text-left font-semibold">
                                  Vakantie {index + 1}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Naam</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.name || 'Onbekend'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Prijs</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.price ? `€${vacation.price}` : 'Prijs op aanvraag'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Bestemming</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.city && vacation.country ? `${vacation.city}, ${vacation.country}` : 
                                   vacation.country || vacation.city || 'Onbekend'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Sterren</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.stars ? `${vacation.stars} ⭐` : 'Niet beoordeeld'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Rating</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.rating || 'Geen rating'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Duur</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.duration ? `${vacation.duration} dagen` : 'Onbekend'}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="border border-gray-200 p-3 font-medium">Reisaanbieder</td>
                              {comparisonData.map(vacation => (
                                <td key={vacation.id} className="border border-gray-200 p-3">
                                  {vacation.aanbieder || 'Onbekend'}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favoriteVacations.map((vacation) => (
                    <div key={vacation['product ID']} className="relative">
                      <div className="absolute top-2 right-2 z-10">
                        <Button
                          size="sm"
                          variant={selectedForComparison.includes(vacation['product ID']) ? "default" : "outline"}
                          onClick={() => handleSelectForComparison(vacation['product ID'])}
                          className="bg-white/90 hover:bg-white"
                        >
                          {selectedForComparison.includes(vacation['product ID']) ? "Geselecteerd" : "Vergelijk"}
                        </Button>
                      </div>
                      <VacationCard
                        vacation={vacation}
                        user={user}
                        isFavorite={true}
                        onFavoriteChange={() => {}}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FavoritesComparisonPage;
